// src/services/ecommerceService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  setDoc,
  query, 
  where, 
  orderBy, 
  limit as limitTo,
  increment,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  startAt,
  endAt
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

class EcommerceService {
  constructor() {
    this.booksCollection = 'pb-books';
  }

  // ============ STORE MANAGEMENT ============
  
  async createStore(storeData, userId) {
    try {
      const defaultSettings = {
        // Basic Store Info
        storeName: storeData.storeName || '',
        tagline: storeData.tagline || '',
        description: storeData.description || '',
        logoUrl: storeData.logoUrl || '',
        favicon: storeData.favicon || '',
        headerBackgroundUrl: storeData.headerBackgroundUrl || '',
        
        // Store Type & Features
        storeType: storeData.storeType || 'general',
        enabledFeatures: {
          cart: true,
          wishlist: true,
          reviews: true,
          inventory: true,
          variants: true,
          digitalProducts: false,
          subscriptions: false,
          bookings: false,
          multiVendor: false,
          giftCards: false,
          loyaltyProgram: false,
          affiliateProgram: false,
          ...storeData.enabledFeatures
        },
        
        // Business Info
        businessType: storeData.businessType || 'retail',
        address: storeData.address || '',
        phone: storeData.phone || '',
        email: storeData.email || '',
        website: storeData.website || '',
        businessHours: storeData.businessHours || {},
        
        // Payment Settings
        paymentMethods: {
          cards: false,
          paypal: false,
          applePay: false,
          googlePay: false,
          bankTransfer: false,
          cash: true,
          crypto: false,
          ...storeData.paymentMethods
        },
        
        // Shipping Settings
        shippingZones: storeData.shippingZones || [],
        shippingMethods: storeData.shippingMethods || [],
        freeShippingThreshold: storeData.freeShippingThreshold || 0,
        
        // Tax Settings
        taxSettings: {
          enabled: false,
          rate: 0,
          inclusive: false,
          ...storeData.taxSettings
        },
        
        // UI/UX Settings
        theme: {
          primaryColor: '#3B82F6',
          secondaryColor: '#8B5CF6',
          layout: 'grid',
          showPrices: true,
          showStock: true,
          showRatings: true,
          ...storeData.theme
        },
        
        // SEO Settings
        seo: {
          metaTitle: '',
          metaDescription: '',
          keywords: [],
          ...storeData.seo
        },
        
        // Analytics
        analytics: {
          googleAnalyticsId: '',
          facebookPixelId: '',
          ...storeData.analytics
        },

        // Store Stats
        productCount: 0,
        orderCount: 0,
        customerCount: 0,
        totalRevenue: 0,
        views: 0,
        
        // Status & Timestamps
        status: 'active',
        published: false,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, this.booksCollection), {
        ...defaultSettings,
        category: 'ecommerce',
        bookType: 'store'
      });
      
      return { id: docRef.id, ...defaultSettings };
    } catch (error) {
      console.error('Error creating store:', error);
      throw new Error('Failed to create store');
    }
  }

  async updateStore(storeId, updates) {
    try {
      const docRef = doc(db, this.booksCollection, storeId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating store:', error);
      throw new Error('Failed to update store');
    }
  }

  async getStore(storeId) {
    try {
      const docRef = doc(db, this.booksCollection, storeId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Store not found');
      }
    } catch (error) {
      console.error('Error getting store:', error);
      throw new Error('Failed to get store');
    }
  }

  async getUserStores(userId) {
    try {
      const q = query(
        collection(db, this.booksCollection),
        where('userId', '==', userId),
        where('category', '==', 'ecommerce'),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const stores = [];
      
      querySnapshot.forEach((doc) => {
        stores.push({ id: doc.id, ...doc.data() });
      });

      return stores;
    } catch (error) {
      console.error('Error getting user stores:', error);
      return [];
    }
  }

  // ============ PRODUCT MANAGEMENT ============
  
  async addProduct(storeId, productData) {
    try {
      const product = {
        ...productData,
        storeId,
        sku: productData.sku || this.generateSKU(),
        status: productData.status || 'active',
        featured: productData.featured || false,
        views: 0,
        sales: 0,
        rating: 0,
        reviewCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, this.booksCollection, storeId, 'products'), 
        product
      );

      // Update store product count
      await this.updateStore(storeId, {
        productCount: increment(1)
      });

      return { id: docRef.id, ...product };
    } catch (error) {
      console.error('Error adding product:', error);
      throw new Error('Failed to add product');
    }
  }

  async updateProduct(storeId, productId, updates) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'products', productId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating product:', error);
      throw new Error('Failed to update product');
    }
  }

  async deleteProduct(storeId, productId) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'products', productId);
      await deleteDoc(docRef);

      // Update store product count
      await this.updateStore(storeId, {
        productCount: increment(-1)
      });

      return { success: true };
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error('Failed to delete product');
    }
  }

  async getProducts(storeId, filters = {}) {
    try {
      let q = query(
        collection(db, this.booksCollection, storeId, 'products'),
        orderBy('createdAt', 'desc')
      );

      // Apply filters
      if (filters.category && filters.category !== 'all') {
        q = query(q, where('category', '==', filters.category));
      }

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.featured !== undefined) {
        q = query(q, where('featured', '==', filters.featured));
      }

      if (filters.limit) {
        q = query(q, limitTo(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        products.push({ id: doc.id, ...doc.data() });
      });

      return products;
    } catch (error) {
      console.error('Error getting products:', error);
      throw new Error('Failed to get products');
    }
  }

  async getProduct(storeId, productId) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'products', productId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {
      console.error('Error getting product:', error);
      throw new Error('Failed to get product');
    }
  }

  async searchProducts(storeId, searchTerm) {
    try {
      const products = await this.getProducts(storeId);
      
      if (!searchTerm) return products;

      const term = searchTerm.toLowerCase();
      return products.filter(product =>
        product.name?.toLowerCase().includes(term) ||
        product.description?.toLowerCase().includes(term) ||
        product.category?.toLowerCase().includes(term) ||
        product.tags?.some(tag => tag.toLowerCase().includes(term))
      );
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }

  // ============ CATEGORY MANAGEMENT ============
  
  async addCategory(storeId, categoryData) {
    try {
      const category = {
        ...categoryData,
        storeId,
        productCount: 0,
        order: categoryData.order || 0,
        status: categoryData.status || 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, this.booksCollection, storeId, 'categories'), 
        category
      );
      
      return { id: docRef.id, ...category };
    } catch (error) {
      console.error('Error adding category:', error);
      throw new Error('Failed to add category');
    }
  }

  async updateCategory(storeId, categoryId, updates) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'categories', categoryId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error('Failed to update category');
    }
  }

  async deleteCategory(storeId, categoryId) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'categories', categoryId);
      await deleteDoc(docRef);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error('Failed to delete category');
    }
  }

  async getCategories(storeId) {
    try {
      const q = query(
        collection(db, this.booksCollection, storeId, 'categories'),
        where('status', '==', 'active'),
        orderBy('order', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const categories = [];
      
      querySnapshot.forEach((doc) => {
        categories.push({ id: doc.id, ...doc.data() });
      });

      return categories;
    } catch (error) {
      console.error('Error getting categories:', error);
      return [];
    }
  }

  // ============ ORDER MANAGEMENT ============
  
  async createOrder(storeId, orderData) {
    try {
      const orderId = doc(collection(db, this.booksCollection, storeId, 'orders'));
      
      const order = {
        ...orderData,
        id: orderId,
        storeId,
        orderNumber: this.generateOrderNumber(),
        status: 'pending',
        paymentStatus: 'pending',
        fulfillmentStatus: 'unfulfilled',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await doc(db, this.booksCollection, storeId, 'orders', orderId).set(order);

      // Update store statistics
      const batch = writeBatch(db);
      
      // Update store order count and revenue
      const storeRef = doc(db, this.booksCollection, storeId);
      batch.update(storeRef, {
        orderCount: increment(1),
        totalRevenue: increment(order.total || 0)
      });

      // Update product sales count for each item
      if (order.items) {
        order.items.forEach(item => {
          const productRef = doc(db, this.booksCollection, storeId, 'products', item.productId);
          batch.update(productRef, {
            sales: increment(item.quantity || 1)
          });
        });
      }

      await batch.commit();

      return { id: orderId, ...order };
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error('Failed to create order');
    }
  }

  async updateOrder(storeId, orderId, updates) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'orders', orderId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating order:', error);
      throw new Error('Failed to update order');
    }
  }

  async getOrders(storeId, filters = {}) {
    try {
      let q = query(
        collection(db, this.booksCollection, storeId, 'orders'),
        orderBy('createdAt', 'desc')
      );

      // Apply filters
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.paymentStatus) {
        q = query(q, where('paymentStatus', '==', filters.paymentStatus));
      }

      if (filters.limit) {
        q = query(q, limitTo(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      const orders = [];
      
      querySnapshot.forEach((doc) => {
        orders.push({ id: doc.id, ...doc.data() });
      });

      return orders;
    } catch (error) {
      console.error('Error getting orders:', error);
      return [];
    }
  }

  async getOrder(storeId, orderId) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'orders', orderId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Order not found');
      }
    } catch (error) {
      console.error('Error getting order:', error);
      throw new Error('Failed to get order');
    }
  }

  // ============ CUSTOMER MANAGEMENT ============
  
  async addCustomer(storeId, customerData) {
    try {
      const customer = {
        ...customerData,
        storeId,
        totalOrders: 0,
        totalSpent: 0,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, this.booksCollection, storeId, 'customers'), 
        customer
      );

      // Update store customer count
      await this.updateStore(storeId, {
        customerCount: increment(1)
      });

      return { id: docRef.id, ...customer };
    } catch (error) {
      console.error('Error adding customer:', error);
      throw new Error('Failed to add customer');
    }
  }

  async getCustomers(storeId) {
    try {
      const q = query(
        collection(db, this.booksCollection, storeId, 'customers'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const customers = [];
      
      querySnapshot.forEach((doc) => {
        customers.push({ id: doc.id, ...doc.data() });
      });

      return customers;
    } catch (error) {
      console.error('Error getting customers:', error);
      return [];
    }
  }

  async updateCustomer(storeId, customerId, updates) {
    try {
      const docRef = doc(db, this.booksCollection, storeId, 'customers', customerId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating customer:', error);
      throw new Error('Failed to update customer');
    }
  }

  // ============ CART MANAGEMENT ============

  async addToCart(storeId, userId, productId, quantity = 1, options = {}) {
    try {
      // Use userId as the cart document ID
      const cartRef = doc(db, this.booksCollection, storeId, 'carts', userId);
      const cartSnap = await getDoc(cartRef);
      
      let cartData;
      if (!cartSnap.exists()) {
        // Create new cart
        cartData = {
          userId,
          items: [],
          totalItems: 0,
          subtotal: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
      } else {
        cartData = cartSnap.data();
      }

      const existingItemIndex = cartData.items.findIndex(
        item => item.productId === productId && 
        JSON.stringify(item.options) === JSON.stringify(options)
      );

      if (existingItemIndex >= 0) {
        // Update existing item quantity
        cartData.items[existingItemIndex].quantity += quantity;
      } else {
        // Add new item
        cartData.items.push({
          productId,
          quantity,
          options,
          addedAt: serverTimestamp()
        });
      }

      // Recalculate totals
      const totals = await this.calculateCartTotals(storeId, cartData.items);

      // Update the cart document
      await setDoc(cartRef, {
        ...cartData,
        ...totals,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw new Error('Failed to add to cart');
    }
  }

  async getCart(storeId, userId) {
    try {
      // Get cart using userId as document ID
      const cartRef = doc(db, this.booksCollection, storeId, 'carts', userId);
      const cartSnap = await getDoc(cartRef);
      
      if (!cartSnap.exists()) {
        return { 
          items: [], 
          totalItems: 0, 
          subtotal: 0,
          userId 
        };
      }

      return { id: cartSnap.id, ...cartSnap.data() };
    } catch (error) {
      console.error('Error getting cart:', error);
      return { 
        items: [], 
        totalItems: 0, 
        subtotal: 0,
        userId 
      };
    }
  }

  async updateCartItem(storeId, userId, productId, quantity, options = {}) {
    try {
      const cartRef = doc(db, this.booksCollection, storeId, 'carts', userId);
      const cartSnap = await getDoc(cartRef);
      
      if (!cartSnap.exists()) {
        throw new Error('Cart not found');
      }

      const cartData = cartSnap.data();
      const itemIndex = cartData.items.findIndex(
        item => item.productId === productId && 
        JSON.stringify(item.options) === JSON.stringify(options)
      );

      if (itemIndex >= 0) {
        if (quantity <= 0) {
          // Remove item
          cartData.items.splice(itemIndex, 1);
        } else {
          // Update quantity
          cartData.items[itemIndex].quantity = quantity;
        }

        // Recalculate totals
        const totals = await this.calculateCartTotals(storeId, cartData.items);

        await updateDoc(cartRef, {
          items: cartData.items,
          ...totals,
          updatedAt: serverTimestamp()
        });

        return { success: true };
      } else {
        throw new Error('Item not found in cart');
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
      throw new Error('Failed to update cart item');
    }
  }

  async removeFromCart(storeId, userId, productId, options = {}) {
    try {
      return await this.updateCartItem(storeId, userId, productId, 0, options);
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw new Error('Failed to remove from cart');
    }
  }

  async clearCart(storeId, userId) {
    try {
      const cartRef = doc(db, this.booksCollection, storeId, 'carts', userId);
      await updateDoc(cartRef, {
        items: [],
        totalItems: 0,
        subtotal: 0,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw new Error('Failed to clear cart');
    }
  }
  
  async getCart(userId) {
    try {
      // For global user cart, use a separate collection
      const cartRef = doc(db, 'pb-user-carts', userId);
      const cartSnap = await getDoc(cartRef);
      
      if (!cartSnap.exists()) {
        return [];
      }

      const cartData = cartSnap.data();
      return cartData.items || [];
    } catch (error) {
      console.error('Error getting cart:', error);
      return [];
    }
  }
  
  async saveCart(userId, cartItems) {
    try {
      const cartRef = doc(db, 'pb-user-carts', userId);
      await setDoc(cartRef, {
        userId,
        items: cartItems,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error saving cart:', error);
      throw new Error('Failed to save cart');
    }
  }
  
  async trackAddToCart(storeId, productId, quantity = 1) {
    try {
      // Track add to cart analytics
      const analyticsRef = collection(db, this.booksCollection, storeId, 'analytics');
      await addDoc(analyticsRef, {
        event: 'add_to_cart',
        productId,
        quantity,
        timestamp: serverTimestamp(),
        
      });
    } catch (error) {
      console.error('Error tracking add to cart:', error);
      // Don't throw error for analytics tracking failures
    }
  }

  async trackProductView(storeId, productId) {
    try {
      // Update product view count
      const productRef = doc(db, this.booksCollection, storeId, 'products', productId);
      await updateDoc(productRef, {
        views: increment(1)
      });

      // Track view analytics
      const analyticsRef = collection(db, this.booksCollection, storeId, 'analytics');
      await addDoc(analyticsRef, {
        event: 'product_view',
        productId,
        timestamp: serverTimestamp(),
        
      });
    } catch (error) {
      console.error('Error tracking product view:', error);
      // Don't throw error for analytics tracking failures
    }
  }

  // ============ REVIEW MANAGEMENT ============
  
  async addReview(storeId, productId, reviewData) {
    try {
      const review = {
        ...reviewData,
        storeId,
        productId,
        status: 'approved',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, this.booksCollection, storeId, 'reviews'), 
        review
      );

      // Update product rating
      await this.updateProductRating(storeId, productId);

      return { id: docRef.id, ...review };
    } catch (error) {
      console.error('Error adding review:', error);
      throw new Error('Failed to add review');
    }
  }

  async getProductReviews(storeId, productId) {
    try {
      const q = query(
        collection(db, this.booksCollection, storeId, 'reviews'),
        where('productId', '==', productId),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const reviews = [];
      
      querySnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });

      return reviews;
    } catch (error) {
      console.error('Error getting product reviews:', error);
      return [];
    }
  }

  // ============ ANALYTICS ============
  
  async getStoreAnalytics(storeId, period = '30d') {
    try {
      const orders = await this.getOrders(storeId);
      const products = await this.getProducts(storeId);
      const customers = await this.getCustomers(storeId);

      // Calculate period-specific data
      const periodMs = this.getPeriodMilliseconds(period);
      const cutoffDate = new Date(Date.now() - periodMs);

      const recentOrders = orders.filter(order => 
        order.createdAt?.toDate() > cutoffDate
      );

      const analytics = {
        overview: {
          totalOrders: orders.length,
          totalProducts: products.length,
          totalCustomers: customers.length,
          totalRevenue: orders.reduce((sum, order) => sum + (order.total || 0), 0)
        },
        period: {
          orders: recentOrders.length,
          revenue: recentOrders.reduce((sum, order) => sum + (order.total || 0), 0),
          averageOrderValue: recentOrders.length > 0 
            ? recentOrders.reduce((sum, order) => sum + (order.total || 0), 0) / recentOrders.length 
            : 0
        },
        topProducts: this.getTopProducts(products, orders),
        ordersByStatus: this.groupOrdersByStatus(orders),
        salesChart: this.generateSalesChart(recentOrders, period)
      };

      return analytics;
    } catch (error) {
      console.error('Error getting store analytics:', error);
      return {};
    }
  }

  // ============ UTILITY METHODS ============
  
  generateSKU() {
    return `SKU-${Date.now()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
  }

  generateOrderNumber() {
    return `ORD-${Date.now()}`;
  }

  async calculateCartTotals(storeId, items) {
    try {
      let subtotal = 0;
      let totalItems = 0;

      for (const item of items) {
        const product = await this.getProduct(storeId, item.productId);
        if (product) {
          subtotal += (product.price || 0) * item.quantity;
          totalItems += item.quantity;
        }
      }

      return { subtotal, totalItems };
    } catch (error) {
      console.error('Error calculating cart totals:', error);
      return { subtotal: 0, totalItems: 0 };
    }
  }

  async updateProductRating(storeId, productId) {
    try {
      const reviews = await this.getProductReviews(storeId, productId);
      
      if (reviews.length === 0) {
        await this.updateProduct(storeId, productId, {
          rating: 0,
          reviewCount: 0
        });
        return;
      }

      const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
      const averageRating = totalRating / reviews.length;

      await this.updateProduct(storeId, productId, {
        rating: Math.round(averageRating * 10) / 10, // Round to 1 decimal
        reviewCount: reviews.length
      });
    } catch (error) {
      console.error('Error updating product rating:', error);
    }
  }

  getPeriodMilliseconds(period) {
    const periods = {
      '1d': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
      '90d': 90 * 24 * 60 * 60 * 1000,
      '1y': 365 * 24 * 60 * 60 * 1000
    };
    return periods[period] || periods['30d'];
  }

  getTopProducts(products, orders) {
    const productSales = {};
    
    orders.forEach(order => {
      if (order.items) {
        order.items.forEach(item => {
          if (!productSales[item.productId]) {
            productSales[item.productId] = 0;
          }
          productSales[item.productId] += item.quantity;
        });
      }
    });

    return Object.entries(productSales)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([productId, sales]) => {
        const product = products.find(p => p.id === productId);
        return { product, sales };
      })
      .filter(item => item.product);
  }

  groupOrdersByStatus(orders) {
    return orders.reduce((acc, order) => {
      const status = order.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }

  generateSalesChart(orders, period) {
    // Generate time-series data for charts
    const intervals = this.getTimeIntervals(period);
    return intervals.map(interval => ({
      date: interval.date,
      sales: orders.filter(order => 
        order.createdAt?.toDate() >= interval.start && 
        order.createdAt?.toDate() < interval.end
      ).reduce((sum, order) => sum + (order.total || 0), 0)
    }));
  }

  getTimeIntervals(period) {
    const now = new Date();
    const intervals = [];
    
    // Implementation depends on period - simplified version
    const days = parseInt(period) || 30;
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
      intervals.push({
        date: date.toISOString().split('T')[0],
        start: new Date(date.setHours(0, 0, 0, 0)),
        end: new Date(date.setHours(23, 59, 59, 999))
      });
    }
    
    return intervals;
  }

  // ============ BULK OPERATIONS ============
  
  async bulkImportProducts(storeId, products) {
    try {
      const batch = writeBatch(db);
      const timestamp = serverTimestamp();
      
      products.forEach(product => {
        const productRef = doc(collection(db, this.booksCollection, storeId, 'products'));
        batch.set(productRef, {
          ...product,
          storeId,
          sku: product.sku || this.generateSKU(),
          status: 'active',
          views: 0,
          sales: 0,
          rating: 0,
          reviewCount: 0,
          createdAt: timestamp,
          updatedAt: timestamp
        });
      });

      await batch.commit();

      // Update store product count
      await this.updateStore(storeId, {
        productCount: increment(products.length)
      });

      return { imported: products.length };
    } catch (error) {
      console.error('Error bulk importing products:', error);
      throw new Error('Failed to import products');
    }
  }

  async bulkUpdateProducts(storeId, updates) {
    try {
      const batch = writeBatch(db);
      const timestamp = serverTimestamp();
      
      updates.forEach(({ productId, data }) => {
        const productRef = doc(db, this.booksCollection, storeId, 'products', productId);
        batch.update(productRef, {
          ...data,
          updatedAt: timestamp
        });
      });

      await batch.commit();
      return { updated: updates.length };
    } catch (error) {
      console.error('Error bulk updating products:', error);
      throw new Error('Failed to bulk update products');
    }
  }

  async initializeStoreSections(storeId) {
    try {
      const batch = writeBatch(db);
      
      // Initialize Hero Section
      const heroRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'hero');
      batch.set(heroRef, {
        type: 'hero',
        enabled: true,
        title: 'Welcome to Our Store',
        subtitle: 'Discover amazing products at unbeatable prices',
        description: 'Shop the latest trends and find everything you need in one place.',
        ctaText: 'Shop Now',
        ctaSecondaryText: 'Learn More',
        heroType: 'single', // single, carousel, video, split
        backgroundImage: '',
        backgroundVideo: '',
        textColor: '#ffffff',
        overlayOpacity: 0.4,
        textPosition: 'center',
        autoplay: true,
        slideDuration: 5000,
        showTrustIndicators: true,
        trustIndicators: [
          { icon: 'Truck', text: 'Free Shipping' },
          { icon: 'Shield', text: 'Secure Payment' },
          { icon: 'Award', text: 'Quality Guaranteed' }
        ],
        slides: [
          {
            id: 'slide-1',
            title: 'Welcome to Our Store',
            subtitle: 'Discover amazing products at unbeatable prices',
            description: 'Shop the latest trends and find everything you need in one place.',
            ctaText: 'Shop Now',
            ctaSecondaryText: 'Learn More',
            backgroundImage: '',
            backgroundVideo: '',
            textColor: '#ffffff',
            overlayOpacity: 0.4,
            textPosition: 'center',
            active: true,
            order: 0
          }
        ],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Initialize Featured Section
      const featuredRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'featured');
      batch.set(featuredRef, {
        type: 'featured',
        enabled: true,
        title: 'Featured Products',
        subtitle: 'Check out our top picks',
        description: 'Hand-picked products just for you',
        displayCount: 8,
        layout: 'grid', // grid, carousel, list
        columns: 4, // for grid layout
        showFilters: false,
        showPrices: true,
        showRatings: true,
        autoRotate: false,
        rotateInterval: 10000,
        sortBy: 'manual', // manual, sales, rating, newest, price
        showViewAllButton: true,
        viewAllText: 'View All Products',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Initialize Promotion Settings
      const promoSettingsRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'promo-settings');
      batch.set(promoSettingsRef, {
        type: 'promo-settings',
        enabled: true,
        title: 'Special Offers',
        subtitle: 'Don\'t miss out on these amazing deals',
        layout: 'banner', // banner, carousel, grid
        autoplay: true,
        slideDuration: 7000,
        showCountdown: true,
        showDiscountBadges: true,
        maxVisible: 3, // for carousel/grid layouts
        animationStyle: 'fade', // fade, slide, zoom
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Initialize with a sample promotion
      const samplePromoRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'sample-promo');
      batch.set(samplePromoRef, {
        type: 'promotion',
        title: 'Grand Opening Sale',
        description: 'Get 20% off your first purchase',
        discountType: 'percentage', // percentage, fixed, freeShipping
        discountValue: 20,
        ctaText: 'Shop Now',
        backgroundColor: '#ef4444',
        textColor: '#ffffff',
        backgroundImage: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
        active: true,
        featured: true,
        order: 0,
        storeId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await batch.commit();
      return { success: true };
    } catch (error) {
      console.error('Error initializing store sections:', error);
      throw new Error('Failed to initialize store sections');
    }
  }

  async createHeroSection(storeId, heroData = {}) {
    try {
      const defaultHero = {
        type: 'hero',
        enabled: true,
        title: 'Welcome to Our Store',
        subtitle: 'Discover amazing products at unbeatable prices',
        description: 'Shop the latest trends and find everything you need in one place.',
        ctaText: 'Shop Now',
        ctaSecondaryText: 'Learn More',
        heroType: 'single',
        backgroundImage: '',
        backgroundVideo: '',
        textColor: '#ffffff',
        overlayOpacity: 0.4,
        textPosition: 'center',
        autoplay: true,
        slideDuration: 5000,
        showTrustIndicators: true,
        trustIndicators: [
          { icon: 'Truck', text: 'Free Shipping' },
          { icon: 'Shield', text: 'Secure Payment' },
          { icon: 'Award', text: 'Quality Guaranteed' }
        ],
        slides: [
          {
            id: 'slide-1',
            title: heroData.title || 'Welcome to Our Store',
            subtitle: heroData.subtitle || 'Discover amazing products at unbeatable prices',
            description: heroData.description || 'Shop the latest trends and find everything you need in one place.',
            ctaText: heroData.ctaText || 'Shop Now',
            ctaSecondaryText: heroData.ctaSecondaryText || 'Learn More',
            backgroundImage: heroData.backgroundImage || '',
            backgroundVideo: heroData.backgroundVideo || '',
            textColor: heroData.textColor || '#ffffff',
            overlayOpacity: heroData.overlayOpacity || 0.4,
            textPosition: heroData.textPosition || 'center',
            active: true,
            order: 0
          }
        ]
      };

      const heroRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'hero');
      await setDoc(heroRef, {
        ...defaultHero,
        ...heroData,
        storeId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { id: 'hero', ...defaultHero, ...heroData };
    } catch (error) {
      console.error('Error creating hero section:', error);
      throw new Error('Failed to create hero section');
    }
  }

  async createFeaturedSection(storeId, featuredData = {}) {
    try {
      const defaultFeatured = {
        type: 'featured',
        enabled: true,
        title: 'Featured Products',
        subtitle: 'Check out our top picks',
        description: 'Hand-picked products just for you',
        displayCount: 8,
        layout: 'grid',
        columns: 4,
        showFilters: false,
        showPrices: true,
        showRatings: true,
        autoRotate: false,
        rotateInterval: 10000,
        sortBy: 'manual',
        showViewAllButton: true,
        viewAllText: 'View All Products'
      };

      const featuredRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'featured');
      await setDoc(featuredRef, {
        ...defaultFeatured,
        ...featuredData,
        storeId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { id: 'featured', ...defaultFeatured, ...featuredData };
    } catch (error) {
      console.error('Error creating featured section:', error);
      throw new Error('Failed to create featured section');
    }
  }

  async createPromotionSettings(storeId, promoData = {}) {
    try {
      const defaultPromoSettings = {
        type: 'promo-settings',
        enabled: true,
        title: 'Special Offers',
        subtitle: 'Don\'t miss out on these amazing deals',
        layout: 'banner',
        autoplay: true,
        slideDuration: 7000,
        showCountdown: true,
        showDiscountBadges: true,
        maxVisible: 3,
        animationStyle: 'fade'
      };

      const promoSettingsRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'promo-settings');
      await setDoc(promoSettingsRef, {
        ...defaultPromoSettings,
        ...promoData,
        storeId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { id: 'promo-settings', ...defaultPromoSettings, ...promoData };
    } catch (error) {
      console.error('Error creating promotion settings:', error);
      throw new Error('Failed to create promotion settings');
    }
  }

  // Add method to check if sections exist
  async checkStoreSections(storeId) {
    try {
      const sectionsRef = collection(db, this.booksCollection, storeId, 'pb-sections');
      const querySnapshot = await getDocs(sectionsRef);
      
      const existingSections = [];
      querySnapshot.forEach((doc) => {
        existingSections.push(doc.id);
      });

      return {
        hasHero: existingSections.includes('hero'),
        hasFeatured: existingSections.includes('featured'),
        hasPromoSettings: existingSections.includes('promo-settings'),
        promotionCount: existingSections.filter(id => 
          !['hero', 'featured', 'promo-settings'].includes(id)
        ).length,
        totalSections: existingSections.length
      };
    } catch (error) {
      console.error('Error checking store sections:', error);
      return {
        hasHero: false,
        hasFeatured: false,
        hasPromoSettings: false,
        promotionCount: 0,
        totalSections: 0
      };
    }
  }

  async getHeroSettings(storeId) {
    try {
      const sectionRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'hero');
      const docSnap = await getDoc(sectionRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        // Return default hero settings
        return {
          enabled: true,
          type: 'single',
          slides: [
            {
              id: 'default',
              title: 'Welcome to Our Store',
              subtitle: 'Discover amazing products at unbeatable prices',
              description: 'Shop the latest trends and find everything you need in one place.',
              ctaText: 'Shop Now',
              ctaSecondaryText: 'Learn More',
              backgroundImage: '',
              backgroundVideo: '',
              textColor: '#ffffff',
              overlayOpacity: 0.4,
              textPosition: 'left',
              active: true
            }
          ],
          autoplay: true,
          slideDuration: 5000,
          showTrustIndicators: true,
          trustIndicators: [
            { icon: 'Truck', text: 'Free Shipping' },
            { icon: 'Shield', text: 'Secure Payment' },
            { icon: 'Award', text: 'Quality Guaranteed' }
          ]
        };
      }
    } catch (error) {
      console.error('Error getting hero settings:', error);
      throw new Error('Failed to load hero settings');
    }
  }

  async updateHeroSettings(storeId, settings) {
    try {
      const sectionRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'hero');
      await setDoc(sectionRef, {
        ...settings,
        type: 'hero',
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating hero settings:', error);
      throw new Error('Failed to update hero settings');
    }
  }

  async getFeaturedSettings(storeId) {
    try {
      const sectionRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'featured');
      const docSnap = await getDoc(sectionRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        // Return default featured settings
        return {
          enabled: true,
          title: 'Featured Products',
          subtitle: 'Check out our top picks',
          displayCount: 8,
          layout: 'grid',
          showFilters: true,
          autoRotate: false,
          rotateInterval: 10000,
          sortBy: 'manual'
        };
      }
    } catch (error) {
      console.error('Error getting featured settings:', error);
      throw new Error('Failed to load featured settings');
    }
  }

  async updateFeaturedSettings(storeId, settings) {
    try {
      const sectionRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'featured');
      await setDoc(sectionRef, {
        ...settings,
        type: 'featured',
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating featured settings:', error);
      throw new Error('Failed to update featured settings');
    }
  }

  async getPromotions(storeId) {
    try {
      const promotionsRef = collection(db, this.booksCollection, storeId, 'pb-sections');
      const q = query(
        promotionsRef,
        where('type', '==', 'promotion'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const promotions = [];
      querySnapshot.forEach((doc) => {
        promotions.push({ id: doc.id, ...doc.data() });
      });

      return promotions;
    } catch (error) {
      console.error('Error getting promotions:', error);
      return [];
    }
  }

  async getPromotionSettings(storeId) {
    try {
      const sectionRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'promo-settings');
      const docSnap = await getDoc(sectionRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        // Return default promo settings
        return {
          enabled: true,
          title: 'Special Offers',
          layout: 'banner',
          autoplay: true,
          slideDuration: 7000,
          showCountdown: true
        };
      }
    } catch (error) {
      console.error('Error getting promotion settings:', error);
      throw new Error('Failed to load promotion settings');
    }
  }

  async updatePromotionSettings(storeId, settings) {
    try {
      const sectionRef = doc(db, this.booksCollection, storeId, 'pb-sections', 'promo-settings');
      await setDoc(sectionRef, {
        ...settings,
        type: 'promo-settings',
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating promotion settings:', error);
      throw new Error('Failed to update promotion settings');
    }
  }

  async createPromotion(storeId, promotionData) {
    try {
      const promotionsRef = collection(db, this.booksCollection, storeId, 'pb-sections');
      const docRef = await addDoc(promotionsRef, {
        ...promotionData,
        type: 'promotion',
        storeId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { id: docRef.id, ...promotionData };
    } catch (error) {
      console.error('Error creating promotion:', error);
      throw new Error('Failed to create promotion');
    }
  }

  async updatePromotion(storeId, promotionId, updates) {
    try {
      const promotionRef = doc(db, this.booksCollection, storeId, 'pb-sections', promotionId);
      await updateDoc(promotionRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating promotion:', error);
      throw new Error('Failed to update promotion');
    }
  }

  async deletePromotion(storeId, promotionId) {
    try {
      const promotionRef = doc(db, this.booksCollection, storeId, 'pb-sections', promotionId);
      await deleteDoc(promotionRef);
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting promotion:', error);
      throw new Error('Failed to delete promotion');
    }
  }

  // ============ SECTION FILE UPLOAD METHODS (using firestoreService) ============
  
  async uploadHeroImage(storeId, file) {
    try {
      const { firestoreService } = await import('./firestoreService');
      const path = `pb-${storeId}/hero/images`;
      return await firestoreService.uploadFile(file, path, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      });
    } catch (error) {
      console.error('Error uploading hero image:', error);
      throw new Error('Failed to upload hero image');
    }
  }

  async uploadHeroVideo(storeId, file) {
    try {
      const { firestoreService } = await import('./firestoreService');
      const path = `pb-${storeId}/hero/videos`;
      return await firestoreService.uploadFile(file, path, {
        maxSize: 50 * 1024 * 1024, // 50MB
        allowedTypes: ['video/mp4', 'video/webm', 'video/ogg']
      });
    } catch (error) {
      console.error('Error uploading hero video:', error);
      throw new Error('Failed to upload hero video');
    }
  }

  async uploadPromotionImage(storeId, file) {
    try {
      const { firestoreService } = await import('./firestoreService');
      const path = `pb-${storeId}/promotions/images`;
      return await firestoreService.uploadFile(file, path, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      });
    } catch (error) {
      console.error('Error uploading promotion image:', error);
      throw new Error('Failed to upload promotion image');
    }
  }

  // Update existing product image upload method to use firestoreService
  async uploadProductImages(storeId, files) {
    try {
      const { firestoreService } = await import('./firestoreService');
      const fileArray = Array.isArray(files) ? files : [files];
      
      const uploadPromises = fileArray.map(file => 
        firestoreService.uploadFile(file, `pb-${storeId}/products/images`, {
          maxSize: 10 * 1024 * 1024, // 10MB
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        })
      );
      
      const results = await Promise.all(uploadPromises);
      return results.map(result => ({
        id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        url: result.url,
        fileName: result.fileName,
        originalName: result.originalFileName,
        fileSize: result.fileSize,
        uploadedAt: result.uploadedAt
      }));
    } catch (error) {
      console.error('Error uploading product images:', error);
      throw new Error('Failed to upload product images');
    }
  }

  // Update existing store logo/favicon upload methods to use firestoreService
  async uploadStoreLogo(storeId, file) {
    try {
      const { firestoreService } = await import('./firestoreService');
      const path = `pb-${storeId}/branding/logo`;
      return await firestoreService.uploadFile(file, path, {
        maxSize: 5 * 1024 * 1024, // 5MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
      });
    } catch (error) {
      console.error('Error uploading store logo:', error);
      throw new Error('Failed to upload store logo');
    }
  }

  async uploadStoreFavicon(storeId, file) {
    try {
      const { firestoreService } = await import('./firestoreService');
      const path = `pb-${storeId}/branding/favicon`;
      return await firestoreService.uploadFile(file, path, {
        maxSize: 1 * 1024 * 1024, // 1MB
        allowedTypes: ['image/x-icon', 'image/png', 'image/gif']
      });
    } catch (error) {
      console.error('Error uploading store favicon:', error);
      throw new Error('Failed to upload store favicon');
    }
  }

  async uploadHeaderBackground(storeId, file) {
    try {
      const { firestoreService } = await import('./firestoreService');
      const path = `pb-${storeId}/branding/header-background`;
      return await firestoreService.uploadFile(file, path, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      });
    } catch (error) {
      console.error('Error uploading header background:', error);
      throw new Error('Failed to upload header background');
    }
  }

  // Method to get all section data at once for template rendering
  async getStoreSections(storeId) {
    try {
      const [heroSettings, featuredSettings, promoSettings, promotions] = await Promise.all([
        this.getHeroSettings(storeId),
        this.getFeaturedSettings(storeId),
        this.getPromotionSettings(storeId),
        this.getPromotions(storeId)
      ]);

      return {
        hero: heroSettings,
        featured: featuredSettings,
        promos: promoSettings,
        promotions: promotions.filter(promo => promo.active)
      };
    } catch (error) {
      console.error('Error getting store sections:', error);
      return {
        hero: { enabled: false },
        featured: { enabled: false },
        promos: { enabled: false },
        promotions: []
      };
    }
  }

  async getRecentActivity(storeId, limit = 10) {
    try {
      const activities = [];
      
      // Get recent orders
      const ordersRef = collection(db, this.booksCollection, storeId, 'orders');
      const ordersQuery = query(
        ordersRef, 
        orderBy('createdAt', 'desc'), 
        limitTo(Math.floor(limit / 2))
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      
      ordersSnapshot.forEach((doc) => {
        const order = doc.data();
        activities.push({
          id: doc.id,
          type: 'order',
          action: 'created',
          title: `New order #${order.orderNumber || doc.id.slice(-6)}`,
          description: `Order for R${order.total?.toFixed(2) || '0.00'} from ${order.customerEmail || 'Guest'}`,
          timestamp: order.createdAt,
          status: order.status || 'pending',
          metadata: {
            orderId: doc.id,
            customerEmail: order.customerEmail,
            total: order.total,
            itemCount: order.items?.length || 0
          }
        });
      });

      // Get recent product additions/updates
      const productsRef = collection(db, this.booksCollection, storeId, 'products');
      const productsQuery = query(
        productsRef, 
        orderBy('updatedAt', 'desc'), 
        limitTo(Math.floor(limit / 2))
      );
      const productsSnapshot = await getDocs(productsQuery);
      
      productsSnapshot.forEach((doc) => {
        const product = doc.data();
        const isNew = product.createdAt === product.updatedAt;
        
        activities.push({
          id: `product-${doc.id}`,
          type: 'product',
          action: isNew ? 'created' : 'updated',
          title: `Product ${isNew ? 'added' : 'updated'}: ${product.name}`,
          description: `${product.name} - R${product.price?.toFixed(2) || '0.00'}`,
          timestamp: product.updatedAt || product.createdAt,
          status: product.status || 'active',
          metadata: {
            productId: doc.id,
            productName: product.name,
            price: product.price,
            category: product.category
          }
        });
      });

      // Sort all activities by timestamp (most recent first)
      activities.sort((a, b) => {
        const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
        const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
        return bTime - aTime;
      });

      return activities.slice(0, limit);
    } catch (error) {
      console.error('Error getting recent activity:', error);
      return [];
    }
  }

  async getStoreAnalytics(storeId, days = 30) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get orders for the period
      const ordersRef = collection(db, this.booksCollection, storeId, 'orders');
      const ordersQuery = query(
        ordersRef,
        where('createdAt', '>=', startDate),
        where('createdAt', '<=', endDate),
        orderBy('createdAt', 'desc')
      );
      
      let orders = [];
      try {
        const ordersSnapshot = await getDocs(ordersQuery);
        orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (error) {
        console.log('No orders found or index missing, using empty array');
        orders = [];
      }

      // Get all products to check inventory
      const products = await this.getProducts(storeId);

      // Calculate metrics
      const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
      const totalOrders = orders.length;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      
      // Count order statuses
      const pendingOrders = orders.filter(order => order.status === 'pending').length;
      const completedOrders = orders.filter(order => order.status === 'completed').length;
      const cancelledOrders = orders.filter(order => order.status === 'cancelled').length;

      // Check low stock items (assuming stock level of 5 or less is low)
      const lowStockCount = products.filter(product => 
        (product.quantity || 0) <= 5 && product.status === 'active'
      ).length;

      // Calculate conversion rate (simplified - orders vs total products viewed)
      const totalProductViews = products.reduce((sum, product) => sum + (product.views || 0), 0);
      const conversionRate = totalProductViews > 0 ? (totalOrders / totalProductViews) * 100 : 0;

      // Get top selling products
      const productSales = {};
      orders.forEach(order => {
        order.items?.forEach(item => {
          const productId = item.productId || item.id;
          if (productId) {
            productSales[productId] = (productSales[productId] || 0) + (item.quantity || 1);
          }
        });
      });

      const topProducts = Object.entries(productSales)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([productId, sales]) => {
          const product = products.find(p => p.id === productId);
          return {
            id: productId,
            name: product?.name || 'Unknown Product',
            sales,
            revenue: sales * (product?.price || 0)
          };
        });

      // Generate daily sales data for chart (last 7 days)
      const chartData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayStart = new Date(date.setHours(0, 0, 0, 0));
        const dayEnd = new Date(date.setHours(23, 59, 59, 999));
        
        const dayOrders = orders.filter(order => {
          const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
          return orderDate >= dayStart && orderDate <= dayEnd;
        });
        
        const dayRevenue = dayOrders.reduce((sum, order) => sum + (order.total || 0), 0);
        
        chartData.push({
          date: dayStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          revenue: dayRevenue,
          orders: dayOrders.length
        });
      }

      return {
        totalRevenue,
        totalOrders,
        averageOrderValue,
        pendingOrders,
        completedOrders,
        cancelledOrders,
        lowStockCount,
        conversionRate,
        topProducts,
        chartData,
        // Additional metrics
        metrics: {
          revenueGrowth: 0, // Calculate vs previous period if needed
          orderGrowth: 0,
          activeProducts: products.filter(p => p.status === 'active').length,
          totalProducts: products.length,
          outOfStockCount: products.filter(p => (p.quantity || 0) === 0).length
        }
      };
    } catch (error) {
      console.error('Error getting store analytics:', error);
      return {
        totalRevenue: 0,
        totalOrders: 0,
        averageOrderValue: 0,
        pendingOrders: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        lowStockCount: 0,
        conversionRate: 0,
        topProducts: [],
        chartData: [],
        metrics: {
          revenueGrowth: 0,
          orderGrowth: 0,
          activeProducts: 0,
          totalProducts: 0,
          outOfStockCount: 0
        }
      };
    }
  }

  async getStore(storeId) {
    try {
      const storeRef = doc(db, this.booksCollection, storeId);
      const storeSnap = await getDoc(storeRef);
      
      if (storeSnap.exists()) {
        return { id: storeSnap.id, ...storeSnap.data() };
      } else {
        console.log('No store found with ID:', storeId);
        return null;
      }
    } catch (error) {
      console.error('Error getting store:', error);
      throw new Error('Failed to get store data');
    }
  }

  // Helper method for dashboard stats summary
  async getDashboardSummary(storeId) {
    try {
      const [analytics, recentActivity, store] = await Promise.all([
        this.getStoreAnalytics(storeId, 30),
        this.getRecentActivity(storeId, 5),
        this.getStore(storeId)
      ]);

      return {
        store,
        analytics,
        recentActivity,
        summary: {
          totalRevenue: analytics.totalRevenue,
          totalOrders: analytics.totalOrders,
          pendingOrders: analytics.pendingOrders,
          lowStockAlerts: analytics.lowStockCount,
          conversionRate: analytics.conversionRate
        }
      };
    } catch (error) {
      console.error('Error getting dashboard summary:', error);
      throw new Error('Failed to load dashboard data');
    }
  }

  // ============ ORDER MANAGEMENT METHODS ============
  
  async getOrders(storeId, filters = {}) {
    try {
      let q = query(
        collection(db, this.booksCollection, storeId, 'orders'),
        orderBy('createdAt', 'desc')
      );

      // Apply filters if provided
      if (filters.status && filters.status !== 'all') {
        q = query(q, where('status', '==', filters.status));
      }

      if (filters.startDate) {
        q = query(q, where('createdAt', '>=', new Date(filters.startDate)));
      }

      if (filters.endDate) {
        q = query(q, where('createdAt', '<=', new Date(filters.endDate)));
      }

      const querySnapshot = await getDocs(q);
      const orders = [];
      
      querySnapshot.forEach((doc) => {
        orders.push({ id: doc.id, ...doc.data() });
      });

      return orders;
    } catch (error) {
      console.error('Error getting orders:', error);
      return [];
    }
  }

  async getOrder(storeId, orderId) {
    try {
      const orderRef = doc(db, this.booksCollection, storeId, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        return { id: orderSnap.id, ...orderSnap.data() };
      } else {
        throw new Error('Order not found');
      }
    } catch (error) {
      console.error('Error getting order:', error);
      throw error;
    }
  }

  async updateOrderStatus(storeId, orderId, status, notes = '') {
    try {
      const orderRef = doc(db, this.booksCollection, storeId, 'orders', orderId);
      await updateDoc(orderRef, {
        status,
        notes,
        updatedAt: serverTimestamp(),
        [`statusHistory.${status}`]: {
          timestamp: serverTimestamp(),
          notes
        }
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating order status:', error);
      throw new Error('Failed to update order status');
    }
    
  }

  async getCategoryStats(storeId, categoryId) {
    try {
      // Fetch all products in this category
      const products = await this.getProducts(storeId, {
        category: categoryId,
        status: 'active'
      });

      // Fetch all orders
      const orders = await this.getOrders(storeId);

      // Count how many times products in this category were sold
      let totalSales = 0;
      let totalRevenue = 0;

      orders.forEach(order => {
        order.items?.forEach(item => {
          if (item.category === categoryId || item.categoryId === categoryId) {
            totalSales += item.quantity || 0;
            totalRevenue += (item.price || 0) * (item.quantity || 1);
          }
        });
      });

      return {
        productCount: products.length,
        totalSales,
        totalRevenue,
        lowStock:
          products.filter(p => (p.quantity || 0) <= 5).length,
        outOfStock:
          products.filter(p => (p.quantity || 0) === 0).length
      };

    } catch (error) {
      console.error("Error getting category stats:", error);
      throw new Error("Failed to load category stats");
    }
  }

}

export const ecommerceService = new EcommerceService();
export default ecommerceService;